import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import apiService from '../../services/api';

const AddSupplier = ({ onSuccess, initialType, editData }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);

    const onClose = () => {
        if (onSuccess) {
            onSuccess();
        } else {
            navigate('/contacts');
        }
    };

    const [contactType, setContactType] = useState(initialType || 'suppliers'); // suppliers, customers, both
    const [subType, setSubType] = useState('individual'); // individual, business

    // Automatically set contact type based on route (if not provided as prop)
    useEffect(() => {
        if (!initialType) {
            if (location.pathname.includes('/customers/add')) {
                setContactType('customers');
            } else if (location.pathname.includes('/suppliers/add')) {
                setContactType('suppliers');
            }
        }
    }, [location.pathname, initialType]);
    
    // Ensure subType is set correctly
    useEffect(() => {
        if (subType === '') {
            setSubType('individual');
        }
    }, [subType]);

    const [formData, setFormData] = useState({
        contact_id: '',
        customer_group: '',
        // Individual fields
        prefix: '',
        first_name: '',
        middle_name: '',
        last_name: '',
        date_of_birth: '',
        // Business fields
        business_name: '',
        // Common fields
        mobile: '',
        alternate_contact_number: '',
        landline: '',
        email: '',
        assigned_to: '',
        // More info
        tax_number: '',
        opening_balance: '0',
        pay_term_value: '',
        pay_term_period: '',
        address_line_1: '',
        address_line_2: '',
        city: '',
        state: '',
        country: '',
        zip_code: '',
        custom_field_1: '',
        custom_field_2: '',
        custom_field_3: '',
        custom_field_4: '',
        shipping_address: '',
        // Contact Person (for Business) or Extra Info (for Individual)
        contact_person_prefix: '',
        contact_person_first_name: '',
        contact_person_last_name: '',
        contact_person_email: '',
        contact_person_mobile: '',
        contact_person_alternate_number: '',
        contact_person_family_number: '',
        department: '',
        designation: '',
        sales_commission_percentage: '',
        allow_login: false,
    });

    const [showMoreInfo, setShowMoreInfo] = useState(false);

    // Populate form if in edit mode
    useEffect(() => {
        if (editData) {
            console.log('Populating AddSupplier with editData:', editData);
            
            // Extract core fields from editData (some might be nested in backend response)
            // Handle variations: { data: [...] }, { data: {...} }, or just [...] or {...}
            const d = Array.isArray(editData.data) ? editData.data[0] : (editData.data || (Array.isArray(editData) ? editData[0] : editData));
            
            if (!d) return;

            setFormData(prev => ({
                ...prev,
                contact_id: d.contact_id || d.id || '',
                prefix: d.prefix || '',
                first_name: d.first_name || d.name || '',
                middle_name: d.middle_name || '',
                last_name: d.last_name || '',
                business_name: d.business_name || d.name || '',
                mobile: d.mobile || d.contact_number || '',
                alternate_contact_number: d.alternate_contact_number || '',
                landline: d.landline || '',
                email: d.email || '',
                assigned_to: d.assigned_to || '',
                date_of_birth: d.dob || d.date_of_birth || '',
                tax_number: d.tax_number || '',
                opening_balance: d.opening_balance || '0',
                pay_term_value: d.pay_term_number || d.pay_term_value || '',
                pay_term_period: d.pay_term_type || d.pay_term_period || '',
                address_line_1: d.address_line_1 || '',
                address_line_2: d.address_line_2 || '',
                city: d.city || '',
                state: d.state || '',
                country: d.country || '',
                zip_code: d.zip_code || '',
                custom_field_1: d.custom_field1 || d.custom_field_1 || '',
                custom_field_2: d.custom_field2 || d.custom_field_2 || '',
                custom_field_3: d.custom_field3 || d.custom_field_3 || '',
                custom_field_4: d.custom_field4 || d.custom_field_4 || '',
            }));

            if (d.type === 'supplier') setContactType('suppliers');
            else if (d.type === 'customer') setContactType('customers');
            else setContactType('both');

            if (d.business_name) setSubType('business');
            else setSubType('individual');
        }
    }, [editData]);

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const handleContactTypeChange = (e) => {
        setContactType(e.target.value);
    };

    const handleSubTypeChange = (type) => {
        setSubType(type);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsLoading(true);
        setError(null);

        try {
            // Map internal state to API expectations
            const payload = {
                type: contactType === 'suppliers' ? 'supplier' : (contactType === 'customers' ? 'customer' : 'both'),
                contact_status: 'active', // default
                ...formData
            };

            // Remove empty fields to avoid API errors (except specific ones)
            Object.keys(payload).forEach(key => {
                if (payload[key] === '' && key !== 'first_name' && key !== 'mobile') {
                    delete payload[key];
                }
            });

            console.log('Submitting Contact Payload:', payload);
            
            let response;
            if (editData) {
                const contactId = editData.id || editData.data?.id;
                response = await apiService.updateContact(contactId, payload);
                console.log('Contact updated successfully:', response);
            } else {
                response = await apiService.createContact(payload);
                console.log('Contact created successfully:', response);
            }
            
            if (onSuccess) {
                onSuccess(response.data || response);
            } else {
                navigate(contactType === 'customers' ? '/contacts/customers' : '/contacts/suppliers');
            }
        } catch (err) {
            console.error('Failed to save contact:', err);
            setError(err.message || 'Failed to save contact');
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="add-product-container contact-form-override">
            {/* Header */}
            <div className="page-header" style={{ marginBottom: '1rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <button
                    onClick={onClose}
                    style={{
                        background: 'white',
                        border: '1px solid #e2e8f0',
                        borderRadius: '8px',
                        padding: '0.5rem 0.75rem',
                        cursor: 'pointer',
                        fontSize: '1.2rem',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        minWidth: '40px',
                        height: '40px'
                    }}
                    title="Back"
                >
                    ←
                </button>
                <h2 style={{ margin: 0, fontSize: '1.25rem', fontWeight: 700 }}>
                    {editData ? 'Edit Contact' : 'Add New Contact'}
                </h2>
            </div>

            <form onSubmit={handleSubmit} className="product-form">
                {/* Main Info Section */}
                <div className="form-section">
                    <div className="form-row">
                        <div className="form-group">
                            <label>Contact type:*</label>
                            <div className="input-wrapper">
                                <span className="input-icon">👤</span>
                                <select
                                    value={contactType}
                                    onChange={handleContactTypeChange}
                                    style={{ paddingLeft: '2.5rem' }}
                                >
                                    <option value="suppliers">Suppliers</option>
                                    <option value="customers">Customers</option>
                                    <option value="both">Both (Supplier & Customer)</option>
                                </select>
                            </div>
                        </div>

                        <div className="form-group" style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: '1.5rem', paddingTop: '1.5rem' }}>
                            <label className="radio-label" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', marginBottom: 0 }}>
                                <input
                                    type="radio"
                                    name="sub_type"
                                    checked={subType === 'individual'}
                                    onChange={() => handleSubTypeChange('individual')}
                                    style={{ margin: 0 }}
                                />
                                Individual
                            </label>
                            <label className="radio-label" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', marginBottom: 0 }}>
                                <input
                                    type="radio"
                                    name="sub_type"
                                    checked={subType === 'business'}
                                    onChange={() => handleSubTypeChange('business')}
                                    style={{ margin: 0 }}
                                />
                                Business
                            </label>
                        </div>

                        <div className="form-group">
                            <label>Contact ID:</label>
                            <div className="input-wrapper">
                                <span className="input-icon">🆔</span>
                                <input
                                    type="text"
                                    name="contact_id"
                                    value={formData.contact_id}
                                    onChange={handleChange}
                                    placeholder="Contact ID"
                                    style={{ paddingLeft: '2.5rem' }}
                                />
                            </div>
                            <small>Leave empty to autogenerate</small>
                        </div>
                        <div className="form-group">
                            <label>Customer Group:</label>
                            <div className="input-wrapper">
                                <span className="input-icon">👥</span>
                                <select
                                    name="customer_group"
                                    value={formData.customer_group}
                                    onChange={handleChange}
                                    style={{ paddingLeft: '2.5rem' }}
                                >
                                    <option value="">None</option>
                                    <option value="local">Local</option>
                                    <option value="foreign">Foreign</option>
                                </select>
                            </div>
                        </div>
                    </div>

                    {/* Individual Fields */}
                    {subType === 'individual' && (
                        <div className="form-row">
                            <div className="form-group" style={{ flex: '0 0 100px' }}>
                                <label>Prefix:</label>
                                <input type="text" name="prefix" value={formData.prefix} onChange={handleChange} placeholder="Mr / Mrs" />
                            </div>
                            <div className="form-group required">
                                <label>First Name:*</label>
                                <input type="text" name="first_name" value={formData.first_name} onChange={handleChange} required />
                            </div>
                            <div className="form-group">
                                <label>Middle name:</label>
                                <input type="text" name="middle_name" value={formData.middle_name} onChange={handleChange} />
                            </div>
                            <div className="form-group">
                                <label>Last Name:</label>
                                <input type="text" name="last_name" value={formData.last_name} onChange={handleChange} />
                            </div>
                        </div>
                    )}

                    {/* Business Fields */}
                    {subType === 'business' && (
                        <div className="form-row">
                            <div className="form-group full-width">
                                <label>Business Name:*</label>
                                <div className="input-wrapper">
                                    <span className="input-icon">🏢</span>
                                    <input
                                        type="text"
                                        name="business_name"
                                        value={formData.business_name}
                                        onChange={handleChange}
                                        required
                                        placeholder="Business Name"
                                        style={{ paddingLeft: '2.5rem' }}
                                    />
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Common Fields */}
                    <div className="form-row">
                        <div className="form-group required">
                            <label>Mobile:*</label>
                            <div className="input-wrapper">
                                <span className="input-icon">📱</span>
                                <input
                                    type="text"
                                    name="mobile"
                                    value={formData.mobile}
                                    onChange={handleChange}
                                    required
                                    placeholder="Mobile"
                                    style={{ paddingLeft: '2.5rem' }}
                                />
                            </div>
                        </div>
                        <div className="form-group">
                            <label>Alt. Number:</label>
                            <div className="input-wrapper">
                                <span className="input-icon">📞</span>
                                <input
                                    type="text"
                                    name="alternate_contact_number"
                                    value={formData.alternate_contact_number}
                                    onChange={handleChange}
                                    placeholder="Alternate"
                                    style={{ paddingLeft: '2.5rem' }}
                                />
                            </div>
                        </div>
                        <div className="form-group">
                            <label>Landline:</label>
                            <div className="input-wrapper">
                                <span className="input-icon">☎️</span>
                                <input
                                    type="text"
                                    name="landline"
                                    value={formData.landline}
                                    onChange={handleChange}
                                    placeholder="Landline"
                                    style={{ paddingLeft: '2.5rem' }}
                                />
                            </div>
                        </div>
                        <div className="form-group">
                            <label>Email:</label>
                            <div className="input-wrapper">
                                <span className="input-icon">✉️</span>
                                <input
                                    type="email"
                                    name="email"
                                    value={formData.email}
                                    onChange={handleChange}
                                    placeholder="Email"
                                    style={{ paddingLeft: '2.5rem' }}
                                />
                            </div>
                        </div>
                    </div>

                    <div className="form-row">
                        <div className="form-group">
                            <label>Assigned to:</label>
                            <div className="input-wrapper">
                                <span className="input-icon">👤</span>
                                <select
                                    name="assigned_to"
                                    value={formData.assigned_to}
                                    onChange={handleChange}
                                    style={{ paddingLeft: '2.5rem' }}
                                >
                                    <option value="">Select User</option>
                                    <option value="Mr Ajay Gogia">Mr Ajay Gogia</option>
                                    <option value="ET Helpdesk">ET Helpdesk</option>
                                    <option value="MR Demo Demo">MR Demo Demo</option>
                                </select>
                            </div>
                        </div>
                        {subType === 'individual' && (
                            <div className="form-group">
                                <label>Date of birth:</label>
                                <div className="input-wrapper">
                                    <span className="input-icon">📅</span>
                                    <input
                                        type="date"
                                        name="date_of_birth"
                                        value={formData.date_of_birth}
                                        onChange={handleChange}
                                        style={{ paddingLeft: '2.5rem' }}
                                    />
                                </div>
                            </div>
                        )}
                        <div className="form-group">
                            <label>Tax number:</label>
                            <div className="input-wrapper">
                                <span className="input-icon">🔢</span>
                                <input type="text" name="tax_number" value={formData.tax_number} onChange={handleChange} placeholder="Tax number" style={{ paddingLeft: '2.5rem' }} />
                            </div>
                        </div>
                        <div className="form-group">
                            <label>Opening Bal:</label>
                            <div className="input-wrapper">
                                <span className="input-icon">💰</span>
                                <input type="number" name="opening_balance" value={formData.opening_balance} onChange={handleChange} placeholder="0" style={{ paddingLeft: '2.5rem' }} />
                            </div>
                        </div>
                    </div>
                </div>

                {/* Toggle Buttons Row */}
                <div style={{ display: 'flex', justifyContent: 'center', gap: '1.5rem', margin: '1rem 0' }}>
                    <button type="button" className="contact-toggle-btn" onClick={() => setShowMoreInfo(!showMoreInfo)}>
                        Address & More Info
                        <span className={`icon-chevron ${showMoreInfo ? 'expanded' : ''}`}>▼</span>
                    </button>
                </div>

                {/* More Info Content */}
                {showMoreInfo && (
                    <div className="form-section no-border" style={{ marginTop: '0.5rem' }}>
                        <div className="form-row">
                            <div className="form-group">
                                <label>Pay term:</label>
                                <div className="input-wrapper" style={{ display: 'flex' }}>
                                    <input type="number" name="pay_term_value" value={formData.pay_term_value} onChange={handleChange} placeholder="Val" style={{ flex: 1, borderTopRightRadius: 0, borderBottomRightRadius: 0 }} />
                                    <select name="pay_term_period" value={formData.pay_term_period} onChange={handleChange} style={{ flex: 1, borderTopLeftRadius: 0, borderBottomLeftRadius: 0, borderLeft: 'none' }}>
                                        <option value="">Period</option>
                                        <option value="days">Days</option>
                                        <option value="months">Months</option>
                                    </select>
                                </div>
                            </div>
                            <div className="form-group">
                                <label>Address line 1:</label>
                                <input type="text" name="address_line_1" value={formData.address_line_1} onChange={handleChange} placeholder="Line 1" />
                            </div>
                            <div className="form-group">
                                <label>Address line 2:</label>
                                <input type="text" name="address_line_2" value={formData.address_line_2} onChange={handleChange} placeholder="Line 2" />
                            </div>
                            <div className="form-group">
                                <label>City:</label>
                                <input type="text" name="city" value={formData.city} onChange={handleChange} placeholder="City" />
                            </div>
                        </div>

                        <div className="form-row">
                            <div className="form-group">
                                <label>State:</label>
                                <input type="text" name="state" value={formData.state} onChange={handleChange} placeholder="State" />
                            </div>
                            <div className="form-group">
                                <label>Country:</label>
                                <input type="text" name="country" value={formData.country} onChange={handleChange} placeholder="Country" />
                            </div>
                            <div className="form-group">
                                <label>Zip Code:</label>
                                <input type="text" name="zip_code" value={formData.zip_code} onChange={handleChange} placeholder="Zip Code" />
                            </div>
                            <div className="form-group">
                                <label>Custom Field 1:</label>
                                <input type="text" name="custom_field_1" value={formData.custom_field_1} onChange={handleChange} placeholder="Field 1" />
                            </div>
                        </div>

                        <div className="form-row">
                            <div className="form-group">
                                <label>Custom Field 3:</label>
                                <input type="text" name="custom_field_3" value={formData.custom_field_3} onChange={handleChange} placeholder="Field 3" />
                            </div>
                            <div className="form-group">
                                <label>Custom Field 4:</label>
                                <input type="text" name="custom_field_4" value={formData.custom_field_4} onChange={handleChange} placeholder="Field 4" />
                            </div>
                        </div>

                        <div style={{ marginTop: '2rem' }}>
                            <label style={{ fontWeight: 600, color: '#475569' }}>Shipping Address</label>
                            <input type="text" placeholder="Search address" style={{ width: '100%', padding: '0.6rem', marginTop: '0.5rem', border: '1px solid #cbd5e1', borderRadius: '4px' }} />
                            <div style={{ width: '100%', height: '200px', background: '#e2e8f0', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#64748b', borderRadius: '4px', marginTop: '0.5rem' }}>
                                This page can't load Google Maps correctly.
                            </div>
                        </div>
                    </div>
                )}

                {error && (
                    <div style={{ color: '#ef4444', backgroundColor: '#fef2f2', padding: '0.75rem', borderRadius: '6px', marginBottom: '1rem', border: '1px solid #fee2e2', fontSize: '13px', fontWeight: '500' }}>
                        ⚠️ {error}
                    </div>
                )}

                <div className="form-actions centered">
                    <button type="button" className="btn-cancel" onClick={onClose} disabled={isLoading}>Close</button>
                    <button type="submit" className="btn-save" disabled={isLoading}>
                        {isLoading ? 'Saving...' : 'Save'}
                    </button>
                </div>
            </form >
        </div >
    );
};

export default AddSupplier;
